import React, { useState } from 'react';
import styled from 'styled-components';
import { migrateUsersToTimeBasedCollections, checkMigrationStatus } from '../utils/migrationUtils';
import { addUserToTimeBasedCollection, findValidUser } from '../utils/firebaseUtils';

const TestContainer = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
`;

const TestSection = styled.div`
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const Button = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
  margin-bottom: 10px;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StatusText = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  font-family: monospace;
  white-space: pre-wrap;
`;

const TestPage: React.FC = () => {
  const [status, setStatus] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleMigration = async () => {
    setIsLoading(true);
    setStatus('마이그레이션 시작...\n');
    
    try {
      await migrateUsersToTimeBasedCollections();
      setStatus(prev => prev + '마이그레이션 완료!\n');
    } catch (error) {
      setStatus(prev => prev + `마이그레이션 오류: ${error}\n`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    setIsLoading(true);
    setStatus('상태 확인 중...\n');
    
    try {
      const migrationStatus = await checkMigrationStatus();
      setStatus(prev => prev + `마이그레이션 상태:\n${JSON.stringify(migrationStatus, null, 2)}\n`);
    } catch (error) {
      setStatus(prev => prev + `상태 확인 오류: ${error}\n`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestAddUser = async () => {
    setIsLoading(true);
    setStatus('테스트 유저 추가 중...\n');
    
    try {
      const testUserData = {
        nickname: `테스트유저_${Date.now()}`,
        avatarNumber: '99',
        age: 25,
        background: '테스트',
        coinCount: 0,
        zoneClearStatus: {
          zone1: '아직안품',
          zone2: '아직안품',
          zone3: '아직안품',
          zone4: '아직안품',
          zone5: '아직안품',
          zone6: '아직안품'
        }
      };

      const docId = await addUserToTimeBasedCollection(testUserData);
      setStatus(prev => prev + `테스트 유저 추가 완료! 문서 ID: ${docId}\n`);
      
      // 추가된 유저 검색 테스트
      const foundUser = await findValidUser(testUserData.nickname, testUserData.avatarNumber);
      if (foundUser) {
        setStatus(prev => prev + `유저 검색 성공: ${foundUser.nickname}\n`);
      } else {
        setStatus(prev => prev + `유저 검색 실패\n`);
      }
    } catch (error) {
      setStatus(prev => prev + `테스트 유저 추가 오류: ${error}\n`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestFindUser = async () => {
    setIsLoading(true);
    setStatus('유저 검색 테스트 중...\n');
    
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) {
        setStatus(prev => prev + '로컬 스토리지에 유저 정보가 없습니다.\n');
        return;
      }

      const userData = JSON.parse(currentUser);
      setStatus(prev => prev + `검색할 유저: ${userData.nickname} (${userData.avatarNumber})\n`);
      
      const foundUser = await findValidUser(userData.nickname, userData.avatarNumber);
      if (foundUser) {
        setStatus(prev => prev + `유저 검색 성공!\n닉네임: ${foundUser.nickname}\n아바타: ${foundUser.avatarNumber}\n코인: ${foundUser.coinCount}\n`);
      } else {
        setStatus(prev => prev + `유저를 찾을 수 없습니다.\n`);
      }
    } catch (error) {
      setStatus(prev => prev + `유저 검색 오류: ${error}\n`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TestContainer>
      <h1>시간 기반 컬렉션 테스트 페이지</h1>
      
      <TestSection>
        <h2>마이그레이션 테스트</h2>
        <p>기존 users 컬렉션을 시간 기반 컬렉션으로 마이그레이션합니다.</p>
        <Button onClick={handleMigration} disabled={isLoading}>
          마이그레이션 실행
        </Button>
        <Button onClick={handleCheckStatus} disabled={isLoading}>
          마이그레이션 상태 확인
        </Button>
      </TestSection>

      <TestSection>
        <h2>시간 기반 컬렉션 테스트</h2>
        <p>새로운 시간 기반 컬렉션에 유저를 추가하고 검색합니다.</p>
        <Button onClick={handleTestAddUser} disabled={isLoading}>
          테스트 유저 추가
        </Button>
        <Button onClick={handleTestFindUser} disabled={isLoading}>
          현재 유저 검색 테스트
        </Button>
      </TestSection>

      <TestSection>
        <h2>상태 및 로그</h2>
        <StatusText>{status || '테스트를 실행하면 여기에 결과가 표시됩니다.'}</StatusText>
      </TestSection>
    </TestContainer>
  );
};

export default TestPage; 